// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

import type { MealInstance, ValidationReasonCode } from "@bitebalance/contracts"
import type { I18nContextValue } from "@/i18n"

type MealSlot = MealInstance["slot"]

/**
 * Documented contract gap (T12/T13): `MealInstanceSchema`
 * (packages/contracts/src/schemas/plans.ts) carries only `recipeId` — there
 * is no title field anywhere reachable over REST. `RecipeSearchResultSchema`
 * (titleDe/titleEn) exists but is only returned by the Amy Gateway
 * `search_recipes` tool, which the web app never calls directly (only Amy's
 * agent runtime does). Rather than fabricate a title or block rendering
 * meal cards entirely, this derives a readable label from the real
 * `recipeId` the API returned (e.g. "rec_vegan_curry" -> "Vegan curry") —
 * a mechanical transform of real data, never invented content. See the
 * final report / commit notes for the follow-up: a recipe-catalog lookup
 * endpoint (or titles inlined on `MealInstance`) closes this gap properly.
 */
export function humanizeRecipeId(recipeId: string): string {
  let words = recipeId.replace(/^rec_/, "").split("_").filter(Boolean)
  // Consumer UX gate (2026-09-02): internal diet-class prefixes and content
  // hashes are implementation detail, never part of a dish's name.
  if (words.length > 1 && ["omni", "pesc", "veg"].includes(words[0])) words = words.slice(1)
  while (words.length > 1 && /^[0-9a-f]{6,}$/.test(words[words.length - 1])) words.pop()
  if (words.length === 0) return recipeId
  return words.map((w, i) => (i === 0 ? w[0].toUpperCase() + w.slice(1) : w)).join(" ")
}


const KNOWN_REASON_BADGES = [
  "VEGAN_MATCH",
  "NUT_FREE",
  "TIME_FIT",
  "PANTRY_REUSE",
  "LOWER_COST",
  "OFFER_MATCH",
] as const
type KnownReasonBadge = (typeof KNOWN_REASON_BADGES)[number]

function isKnownReasonBadge(code: string): code is KnownReasonBadge {
  return (KNOWN_REASON_BADGES as readonly string[]).includes(code)
}

/** Localizes a meal's `reasonCodes` badge; unknown codes fall back to a humanized (not raw) label. */
export function reasonBadgeLabel(code: string, t: I18nContextValue["t"]): string {
  if (isKnownReasonBadge(code)) {
    return t(`views.reasonBadges.${code}`)
  }
  return humanizeRecipeId(code.toLowerCase())
}

const KNOWN_VALIDATION_REASONS: ValidationReasonCode[] = [
  "ALLERGEN_CONFLICT",
  "HARD_EXCLUSION",
  "MISSING_COMPOSITION",
  "NUTRITION_OUT_OF_RANGE",
  "TIME_LIMIT_EXCEEDED",
  "BATCH_YIELD_MISMATCH",
]

/**
 * Localizes a deterministic validator failure reason code. Per
 * instructions.md T12 ("Validator failures render localized reason
 * messages — never raw codes") this always returns human copy, falling
 * back to a generic safety message for any code outside the six stable
 * reason codes rather than ever printing the enum value itself.
 */
export function validationReasonMessage(code: string, t: I18nContextValue["t"]): string {
  const known = KNOWN_VALIDATION_REASONS.find(c => c === code)
  if (known) return t(`views.validationReasons.${known}`)
  return t("views.validationReasons.generic")
}

export function slotLabel(slot: MealSlot, t: I18nContextValue["t"]): string {
  return t(`views.slots.${slot}`)
}

/** ADR-023: snack slots render visually lighter than main-meal slots. */
export function isSnackSlot(slot: MealSlot): boolean {
  return slot === "morning_snack" || slot === "afternoon_snack" || slot === "snack"
}

export function planningModeLabel(
  mode: "daily" | "batch" | "hybrid",
  t: I18nContextValue["t"]
): string {
  if (mode === "daily") return t("onboarding.step10.daily")
  if (mode === "batch") return t("onboarding.step10.batch")
  return t("onboarding.step10.hybrid")
}
